import React, { createContext, useContext, useState, useCallback, ReactNode, useRef, useEffect } from 'react';

export type NotesPath = 'structured' | 'unstructured' | null;
export type NoteTemplate = 'soap_session' | 'quick_note' | 'check_in' | 'raw_transcript';

export interface StructuredNoteContent {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

export interface NoteVisibility {
  subjective: boolean;
  objective: boolean;
  assessment: boolean;
  plan: boolean;
}

// Existing note data for edit mode
export interface ExistingNoteData {
  note_id: string;
  title: string;
  session_date: string;
  tags: string[];
  sections: {
    subjective?: { content?: string; client_visible?: boolean };
    objective?: { coach_observations?: string; client_visible?: boolean };
    assessment?: { content?: string; client_visible?: boolean };
    plan?: { content?: string; client_visible?: boolean };
  };
}

interface NotesWindowContextType {
  // Window state
  isOpen: boolean;
  isMinimized: boolean;

  // Edit mode
  isEditMode: boolean;
  editingNoteId: string | null;

  // Path selection
  currentPath: NotesPath;
  selectedTemplate: NoteTemplate | null;

  // Client context
  clientId: string | null;
  clientName: string | null;

  // Tags for edit mode (passed from existing note)
  initialTags: string[];

  // Structured note state (Path 1)
  fieldContents: StructuredNoteContent;
  fieldVisibility: NoteVisibility;
  activeField: keyof StructuredNoteContent | null;
  lockedTargetField: keyof StructuredNoteContent | null;
  noteTitle: string;
  noteDate: string;

  // Unstructured capture state (Path 2)
  unstructuredContent: string;

  // Transcription state (both paths)
  isRecording: boolean;
  isPaused: boolean;
  recordingDuration: number;
  interimTranscript: string;  // Real-time preview of current speech

  // Actions
  openWindow: (clientId: string, clientName: string) => void;
  openWindowForEdit: (clientId: string, clientName: string, existingNote: ExistingNoteData) => void;
  closeWindow: () => void;
  toggleMinimized: () => void;

  // Path actions
  selectPath: (path: NotesPath) => void;
  selectTemplate: (template: NoteTemplate) => void;

  // Structured note actions
  setActiveField: (field: keyof StructuredNoteContent | null) => void;
  updateFieldContent: (field: keyof StructuredNoteContent, content: string) => void;
  toggleFieldVisibility: (field: keyof StructuredNoteContent) => void;
  setNoteTitle: (title: string) => void;
  setNoteDate: (date: string) => void;

  // Unstructured capture actions
  setUnstructuredContent: (content: string) => void;
  appendToUnstructured: (text: string) => void;

  // Transcription actions
  startRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  stopRecording: () => void;

  // Utility
  resetWindow: () => void;
  convertToUnstructured: () => void;
}

const NotesWindowContext = createContext<NotesWindowContextType | null>(null);

export const useNotesWindow = () => {
  const context = useContext(NotesWindowContext);
  if (!context) {
    throw new Error('useNotesWindow must be used within a NotesWindowProvider');
  }
  return context;
};

interface NotesWindowProviderProps {
  children: ReactNode;
}

const DEFAULT_FIELD_CONTENTS: StructuredNoteContent = {
  subjective: '',
  objective: '',
  assessment: '',
  plan: ''
};

const DEFAULT_VISIBILITY: NoteVisibility = {
  subjective: false,  // Private by default
  objective: false,   // Private by default
  assessment: false,  // Private by default
  plan: true          // Shared by default
};

export const NotesWindowProvider: React.FC<NotesWindowProviderProps> = ({ children }) => {
  // Window state
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  // Edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);

  // Path selection
  const [currentPath, setCurrentPath] = useState<NotesPath>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<NoteTemplate | null>(null);

  // Client context
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [initialTags, setInitialTags] = useState<string[]>([]);

  // Structured note state
  const [fieldContents, setFieldContents] = useState<StructuredNoteContent>(DEFAULT_FIELD_CONTENTS);
  const [fieldVisibility, setFieldVisibility] = useState<NoteVisibility>(DEFAULT_VISIBILITY);
  const [activeField, setActiveFieldState] = useState<keyof StructuredNoteContent | null>(null);
  const [lockedTargetField, setLockedTargetField] = useState<keyof StructuredNoteContent | null>(null);
  const [noteTitle, setNoteTitleState] = useState('');
  const [noteDate, setNoteDateState] = useState(new Date().toISOString().split('T')[0]);

  // Unstructured capture state
  const [unstructuredContent, setUnstructuredContentState] = useState('');

  // Transcription state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [interimTranscript, setInterimTranscript] = useState('');

  // Speech recognition ref
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const durationIntervalRef = useRef<number | null>(null);

  // Refs to hold current values for speech recognition callback (avoids stale closures)
  const currentPathRef = useRef<NotesPath>(currentPath);
  const selectedTemplateRef = useRef<NoteTemplate | null>(selectedTemplate);
  const lockedTargetFieldRef = useRef<keyof StructuredNoteContent | null>(lockedTargetField);
  const isRecordingRef = useRef(isRecording);
  const isPausedRef = useRef(isPaused);

  // Keep refs in sync with state
  useEffect(() => { currentPathRef.current = currentPath; }, [currentPath]);
  useEffect(() => { selectedTemplateRef.current = selectedTemplate; }, [selectedTemplate]);
  useEffect(() => { lockedTargetFieldRef.current = lockedTargetField; }, [lockedTargetField]);
  useEffect(() => { isRecordingRef.current = isRecording; }, [isRecording]);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  // Initialize speech recognition (only once on mount)
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let finalTranscript = '';
        let interimText = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript + ' ';
          } else {
            // Interim result - show real-time preview
            interimText += result[0].transcript;
          }
        }

        // Update interim preview (shows what's being spoken right now)
        setInterimTranscript(interimText);

        if (finalTranscript) {
          // Clear interim when we get final
          setInterimTranscript('');

          // Read current values from refs (not stale closure values)
          const path = currentPathRef.current;
          const template = selectedTemplateRef.current;
          const targetField = lockedTargetFieldRef.current;

          // Append to appropriate target based on current path and template
          // Quick Note uses unstructuredContent even though it's in 'structured' path
          if (path === 'structured' && template !== 'quick_note' && targetField) {
            // SOAP/Check-in: append to locked field
            setFieldContents(prev => ({
              ...prev,
              [targetField]: prev[targetField] + finalTranscript
            }));
          } else if (path === 'unstructured' || template === 'quick_note') {
            // AI-Assisted or Quick Note: append to unstructured content
            setUnstructuredContentState(prev => prev + finalTranscript);
          }
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        if (event.error !== 'no-speech') {
          setIsRecording(false);
          setIsPaused(false);
        }
      };

      recognition.onend = () => {
        // Restart if still recording and not paused (read from refs)
        if (isRecordingRef.current && !isPausedRef.current && recognitionRef.current) {
          try {
            recognitionRef.current.start();
          } catch (e) {
            // Ignore - might already be started
          }
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, []); // Empty deps - only run once on mount

  // Actions
  const openWindow = useCallback((cId: string, cName: string) => {
    setClientId(cId);
    setClientName(cName);
    setIsOpen(true);
    setIsMinimized(false);
    // Reset to path selection
    setCurrentPath(null);
    setSelectedTemplate(null);
    // Clear edit mode
    setIsEditMode(false);
    setEditingNoteId(null);
    setInitialTags([]);
  }, []);

  const openWindowForEdit = useCallback((cId: string, cName: string, existingNote: ExistingNoteData) => {
    setClientId(cId);
    setClientName(cName);
    setIsOpen(true);
    setIsMinimized(false);

    // Set edit mode
    setIsEditMode(true);
    setEditingNoteId(existingNote.note_id);

    // Skip path/template selection - go straight to SOAP editor
    setCurrentPath('structured');
    setSelectedTemplate('soap_session');

    // Pre-populate from existing note
    setNoteTitleState(existingNote.title || '');
    setNoteDateState(existingNote.session_date || new Date().toISOString().split('T')[0]);

    // Pre-populate field contents
    setFieldContents({
      subjective: existingNote.sections?.subjective?.content || '',
      objective: existingNote.sections?.objective?.coach_observations || '',
      assessment: existingNote.sections?.assessment?.content || '',
      plan: existingNote.sections?.plan?.content || ''
    });

    // Pre-populate visibility
    setFieldVisibility({
      subjective: existingNote.sections?.subjective?.client_visible ?? false,
      objective: existingNote.sections?.objective?.client_visible ?? false,
      assessment: existingNote.sections?.assessment?.client_visible ?? false,
      plan: existingNote.sections?.plan?.client_visible ?? true
    });

    // Pre-populate tags
    setInitialTags(existingNote.tags || []);
  }, []);

  const closeWindow = useCallback(() => {
    // Stop any recording
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    setIsRecording(false);
    setIsPaused(false);
    setIsOpen(false);
  }, [isRecording]);

  const toggleMinimized = useCallback(() => {
    setIsMinimized(prev => !prev);
  }, []);

  const selectPath = useCallback((path: NotesPath) => {
    setCurrentPath(path);
    if (path === 'unstructured') {
      setSelectedTemplate('raw_transcript');
    }
  }, []);

  const selectTemplate = useCallback((template: NoteTemplate) => {
    setSelectedTemplate(template);
    // Reset field contents when selecting new template
    setFieldContents(DEFAULT_FIELD_CONTENTS);
    setFieldVisibility(DEFAULT_VISIBILITY);
    setActiveFieldState(null);
    setLockedTargetField(null);
  }, []);

  const setActiveField = useCallback((field: keyof StructuredNoteContent | null) => {
    // Only allow changing active field if not recording
    if (!isRecording) {
      setActiveFieldState(field);
    }
  }, [isRecording]);

  const updateFieldContent = useCallback((field: keyof StructuredNoteContent, content: string) => {
    setFieldContents(prev => ({
      ...prev,
      [field]: content
    }));
  }, []);

  const toggleFieldVisibility = useCallback((field: keyof StructuredNoteContent) => {
    setFieldVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  }, []);

  const setNoteTitle = useCallback((title: string) => {
    setNoteTitleState(title);
  }, []);

  const setNoteDate = useCallback((date: string) => {
    setNoteDateState(date);
  }, []);

  const setUnstructuredContent = useCallback((content: string) => {
    setUnstructuredContentState(content);
  }, []);

  const appendToUnstructured = useCallback((text: string) => {
    setUnstructuredContentState(prev => prev + text);
  }, []);

  // Transcription actions
  const startRecording = useCallback(() => {
    if (!recognitionRef.current) {
      console.error('Speech recognition not available');
      return;
    }

    // For SOAP/Check-in notes, lock the active field
    // Quick Note doesn't use field locking (goes to unstructuredContent)
    // Set BOTH the ref (synchronous, for callback) and state (for UI)
    if (currentPath === 'structured' && selectedTemplate !== 'quick_note' && activeField) {
      lockedTargetFieldRef.current = activeField;  // Immediate for callback
      setLockedTargetField(activeField);           // For UI update
    }

    // Set refs immediately (synchronous) before starting recognition
    isRecordingRef.current = true;
    isPausedRef.current = false;

    setIsRecording(true);
    setIsPaused(false);
    setRecordingDuration(0);

    // Start duration counter
    durationIntervalRef.current = window.setInterval(() => {
      setRecordingDuration(prev => prev + 1);
    }, 1000);

    try {
      recognitionRef.current.start();
    } catch (e) {
      console.error('Error starting recognition:', e);
    }
  }, [currentPath, selectedTemplate, activeField]);

  const pauseRecording = useCallback(() => {
    if (recognitionRef.current && isRecording) {
      isPausedRef.current = true;  // Sync ref
      recognitionRef.current.stop();
      setIsPaused(true);
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    }
  }, [isRecording]);

  const resumeRecording = useCallback(() => {
    if (recognitionRef.current && isPaused) {
      isPausedRef.current = false;  // Sync ref
      setIsPaused(false);
      // Restart duration counter
      durationIntervalRef.current = window.setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.error('Error resuming recognition:', e);
      }
    }
  }, [isPaused]);

  const stopRecording = useCallback(() => {
    // Sync refs immediately
    isRecordingRef.current = false;
    isPausedRef.current = false;
    lockedTargetFieldRef.current = null;

    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    setIsRecording(false);
    setIsPaused(false);
    setLockedTargetField(null);  // Unlock field
  }, []);

  const resetWindow = useCallback(() => {
    setCurrentPath(null);
    setSelectedTemplate(null);
    setFieldContents(DEFAULT_FIELD_CONTENTS);
    setFieldVisibility(DEFAULT_VISIBILITY);
    setActiveFieldState(null);
    setLockedTargetField(null);
    setNoteTitleState('');
    setNoteDateState(new Date().toISOString().split('T')[0]);
    setUnstructuredContentState('');
    setRecordingDuration(0);
    // Clear edit mode
    setIsEditMode(false);
    setEditingNoteId(null);
    if (recognitionRef.current && isRecording) {
      recognitionRef.current.stop();
    }
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
    }
    setIsRecording(false);
    setIsPaused(false);
  }, [isRecording]);

  const convertToUnstructured = useCallback(() => {
    // Take all structured content and move to unstructured
    const combined = [
      fieldContents.subjective && `SUBJECTIVE:\n${fieldContents.subjective}`,
      fieldContents.objective && `OBJECTIVE:\n${fieldContents.objective}`,
      fieldContents.assessment && `ASSESSMENT:\n${fieldContents.assessment}`,
      fieldContents.plan && `PLAN:\n${fieldContents.plan}`
    ].filter(Boolean).join('\n\n');

    setUnstructuredContentState(combined);
    setCurrentPath('unstructured');
    setFieldContents(DEFAULT_FIELD_CONTENTS);
    setActiveFieldState(null);
    setLockedTargetField(null);
  }, [fieldContents]);

  const value: NotesWindowContextType = {
    isOpen,
    isMinimized,
    isEditMode,
    editingNoteId,
    currentPath,
    selectedTemplate,
    clientId,
    clientName,
    initialTags,
    fieldContents,
    fieldVisibility,
    activeField,
    lockedTargetField,
    noteTitle,
    noteDate,
    unstructuredContent,
    isRecording,
    isPaused,
    recordingDuration,
    interimTranscript,
    openWindow,
    openWindowForEdit,
    closeWindow,
    toggleMinimized,
    selectPath,
    selectTemplate,
    setActiveField,
    updateFieldContent,
    toggleFieldVisibility,
    setNoteTitle,
    setNoteDate,
    setUnstructuredContent,
    appendToUnstructured,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetWindow,
    convertToUnstructured
  };

  return (
    <NotesWindowContext.Provider value={value}>
      {children}
    </NotesWindowContext.Provider>
  );
};
